
import { ReferralClick, ClickStatistics } from './types';
import { getUserReferrals } from './referralService';

// Mock data store
let referralClicks: ReferralClick[] = [];

// Track a click on a referral link
export const trackReferralClick = async (
  referralCode: string,
  referrerId: string,
  source?: string,
  medium?: string,
  campaign?: string,
  origin?: string,
  landingPage?: string
): Promise<ReferralClick> => {
  const newClick: ReferralClick = {
    id: `click-${Date.now()}`,
    referralCode,
    referrerId,
    clickedAt: new Date().toISOString(),
    ipAddress: '127.0.0.1', // In a real app, this would be the actual IP
    userAgent: navigator.userAgent,
    source,
    medium,
    campaign,
    origin,
    landingPage,
    converted: false,
    timestamp: new Date().toISOString(),
    clickDate: new Date().toISOString()
  };
  
  referralClicks.push(newClick);
  return newClick;
};

// Track a successful referral conversion
export const trackReferral = async (
  referralCode: string,
  refereeId: string
): Promise<boolean> => {
  // Find the most recent click for this code
  const clickIndex = referralClicks.findIndex(click => 
    click.referralCode === referralCode && !click.converted
  );
  
  if (clickIndex !== -1) {
    // Mark as converted
    referralClicks[clickIndex] = {
      ...referralClicks[clickIndex],
      converted: true,
      convertedAt: new Date().toISOString(),
      visitorInfo: {
        converted: true,
        signupDate: new Date().toISOString()
      }
    };
    
    return true;
  }
  
  return false;
};

// Validate a referral code
export const validateReferralCode = async (code: string): Promise<boolean> => {
  // In a real app, this would check against valid referral codes in the database
  return code && code.length > 5;
};

// Get referral by code
export const getReferralByCode = async (code: string): Promise<any> => {
  // In a real app, this would fetch the referral from a database
  return {
    code,
    valid: true,
    referrerId: 'user-123',
    createdAt: new Date().toISOString()
  };
};

// Update referral status
export const updateReferralStatus = async (
  referralId: string,
  status: string
): Promise<boolean> => {
  console.log(`Updating referral ${referralId} to status: ${status}`);
  // In a real app, this would update the status in the database
  return true;
};

// Get list of referrals
export const getReferralsList = async (userId: string): Promise<any[]> => {
  return getUserReferrals(userId);
};

// Get all referral clicks
export const getAllReferralClicks = async (): Promise<ReferralClick[]> => {
  return [...referralClicks];
};

// Get referral clicks by referral code
export const getReferralClicksByCode = async (
  code: string
): Promise<ReferralClick[]> => {
  return referralClicks.filter(click => click.referralCode === code);
};

// Get user referral clicks
export const getUserReferralClicks = async (
  userId: string
): Promise<ReferralClick[]> => {
  return referralClicks.filter(click => click.referrerId === userId);
};

// Get referral click statistics
export const getReferralClickStatistics = async (
  userId: string
): Promise<ClickStatistics> => {
  const userClicks = await getUserReferralClicks(userId);
  
  // Calculate total and unique clicks
  const totalClicks = userClicks.length;
  const uniqueIps = new Set(userClicks.map(click => click.ipAddress).filter(Boolean)).size;
  
  // Calculate conversion rate
  const convertedClicks = userClicks.filter(click => click.converted).length;
  const conversionRate = totalClicks > 0 ? (convertedClicks / totalClicks) * 100 : 0;
  
  // Create source breakdown
  const sourceBreakdown: Record<string, number> = {};
  userClicks.forEach(click => {
    const source = click.source || 'direct';
    sourceBreakdown[source] = (sourceBreakdown[source] || 0) + 1;
  });
  
  // Create time distribution
  const timeDistribution: Record<string, number> = {};
  userClicks.forEach(click => {
    const date = new Date(click.clickedAt).toLocaleDateString();
    timeDistribution[date] = (timeDistribution[date] || 0) + 1;
  });
  
  return {
    totalClicks,
    uniqueClicks: uniqueIps,
    conversionRate,
    sourceBreakdown,
    timeDistribution
  };
};
